import type {
    Vehicle,
    VehicleDerivative,
    AdditionalVehicleDetailsFormInputs,
} from '@/features/vehiclesearch';
import { toDateKey } from './dateKey';

/**
 * `null` means the form seeded the field empty behind an unaccepted MOT estimate (keep the
 * stored reading); `undefined` means the user cleared it. Never collapse the two branches.
 */
export const mergeOdometerReading = (
    submitted: number | null | undefined,
    stored: number | undefined
): number | undefined => {
    if (typeof submitted === 'number') {
        return submitted;
    }
    if (submitted === undefined) {
        return undefined;
    }
    // A stored reading the schema would reject is dropped so clearing it in the UI sticks.
    return typeof stored === 'number' && Number.isFinite(stored) && stored >= 0
        ? stored
        : undefined;
};

/**
 * The form is the authority for the fields it owns: an absent value is a clear and is written
 * as absent. Do not reintroduce `|| vehicle.<field>` fallbacks; the odometer pair is the exception.
 */
export const updateExistingVehicle = (
    vehicle: Vehicle,
    data: AdditionalVehicleDetailsFormInputs
): Vehicle => {
    return {
        ...vehicle,
        vin: data.vin,
        // toDateKey, not toISOString: flatpickr returns local-midnight Dates, and a UTC
        // re-projection stores the previous calendar day during BST.
        date_of_first_registration: toDateKey(data.registrationDate) ?? undefined,
        last_mot_date: toDateKey(data.lastMotDate) ?? undefined,
        last_service_date: toDateKey(data.lastServiceDate) ?? undefined,
        // Never write an orphan mileage with no date.
        last_service_mileage: data.lastServiceDate ? data.lastServiceMileage : undefined,
        current_status: {
            ...vehicle.current_status,
            vrm: data.vrm,
            // Cleared only alongside `vrm`; otherwise `vrm` wins the display `??` and the
            // CarWeb formatting is worth keeping.
            formatted_vrm: data.vrm ? vehicle.current_status?.formatted_vrm : undefined,
            mileage: mergeOdometerReading(data.mileage, vehicle.current_status?.mileage),
            annual_mileage: mergeOdometerReading(
                data.annualMileage,
                vehicle.current_status?.annual_mileage
            ),
        },
    };
};

export const convertDerivativeToVehicle = (
    derivative: VehicleDerivative,
    data: AdditionalVehicleDetailsFormInputs
): Vehicle => {
    return {
        type: 'vehicle' as const,
        vin: data.vin,
        // No fabricated fallback date: an absent value lets vehicleScheduleContext fall
        // through to first_year_of_manufacture, which is the accurate answer for a derivative.
        date_of_first_registration: toDateKey(data.registrationDate) ?? undefined,
        last_mot_date: toDateKey(data.lastMotDate) ?? undefined,
        last_service_date: toDateKey(data.lastServiceDate) ?? undefined,
        // Never write an orphan mileage with no date.
        last_service_mileage: data.lastServiceDate ? data.lastServiceMileage : undefined,
        current_status: {
            vrm: data.vrm,
            mileage: data.mileage ?? undefined,
            annual_mileage: data.annualMileage ?? undefined,
        },
        derivative: { ...derivative },
        full_description:
            derivative.short_name || `${derivative.make.name} ${derivative.model.name}`,
    };
};
